import {
    AiOutlineCalendar,
    AiOutlineClose,
    AiOutlineEdit,
    AiOutlinePlus,
    AiOutlineReload,
    AiOutlineThunderbolt,
    AiOutlineWarning,
} from 'react-icons/ai';
import TextareaAutosize from 'react-textarea-autosize';
import {
    Link as ChakraLink,
    Box,
    BoxProps,
    Button,
    ButtonGroup,
    Center,
    chakra,
    FormControl,
    FormLabel,
    Grid,
    GridItem,
    Heading,
    HStack,
    Icon,
    IconButton,
    Image,
    Input,
    InputGroup,
    InputRightElement,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    SimpleGrid,
    StackDivider,
    Switch,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Tag,
    TagLabel,
    TagCloseButton,
    Text,
    Textarea,
    Tooltip,
    VStack,
    Wrap,
    VisuallyHidden,
    Popover,
    PopoverContent,
    PopoverBody,
    PopoverTrigger,
    PopoverHeader,
    PopoverFooter,
    Select,
    Divider,
    AbsoluteCenter,
    Radio,
} from '@chakra-ui/react';
import React from 'react';
import { BsArrowRight, BsArrowRightShort } from 'react-icons/bs';
import { BiChevronDown } from 'react-icons/bi';
import { Graphics } from '../../../../../config/svg';
import { ReportingViewBaseConfig } from '../../base';
import { ReportGenerateViewProps } from './reportHomeProps';
import { RiAttachment2 } from 'react-icons/ri';
import { MdOutlineAlternateEmail, MdOutlineRateReview, MdSettings } from 'react-icons/md';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { HomeOptionContainerProps, HomeOptionViewProps } from './option';
import { TemplateSelectContainerProps, TemplateSelectViewProps } from '../template';

export function createReportGenerateView(
    config: ReportingViewBaseConfig & {
        Container: {
            Option: React.FC<HomeOptionContainerProps>;
            Template: {
                Select: React.FC<TemplateSelectContainerProps>;
            };
        };
    }
): React.FC<ReportGenerateViewProps> {
    const {
        Container: {
            Option: OptionContainer,
            Template: { Select: TemplateSelectContainer },
        },
        Layout: {
            Centered: {
                Component: {
                    Root: LayoutRoot,
                    Header: LayoutHeader,
                    Content: LayoutContent,
                    Body: LayoutBody,
                },
            },
        },
        UI: {
            Application: { Link },
            Date: {
                DatePicker: {
                    Container: DateContainer,
                    Absolute: DateAbsolute,
                    Relative: DateRelative,
                },
            },
        },
    } = config;

    // const containerStyle = {
    //     maxW: { base: '90vw', lg: '85vw', xl: '75vw', '2xl': '72rem' },
    // } satisfies Partial<BoxProps>;

    // const recommendations = [
    //     {
    //         id: '1',
    //         title: `I want to automate KYC with AI`,
    //     },
    //     {
    //         id: '2',
    //         title: `I want to automate customer support with AI`,
    //     },
    //     {
    //         id: '3',
    //         title: `I want to automate customer onboarding with AI`,
    //     },
    //     {
    //         id: '4',
    //         title: `I want to automate developer maintenance with AI`,
    //     },
    // ];

    const ChakraTextareaAutosize = chakra(TextareaAutosize, {
        baseStyle: {
            // any default Chakra styles you want here
            // e.g. fontSize: "md", etc.
        },
    });

    const MAX_DISPLAY_FILES = 5;

    const TemplateSelect: React.FC<TemplateSelectViewProps> = (props) => {
        return (
            <VStack align="start" w="full" spacing={4}>
                <HStack w="full" justify="space-between" fontSize="sm">
                    <Text
                        color="whiteAlpha.500"
                        fontWeight="bold"
                        textTransform="uppercase"
                        letterSpacing="wide"
                        fontSize="sm"
                    >
                        Templates
                    </Text>
                    <ChakraLink as={Link}>
                        <Text>View all</Text>
                    </ChakraLink>
                </HStack>
                <SimpleGrid w="full" gap={4} columns={{ base: 1, md: 2 }}>
                    {props.items.map((item) => (
                        <VStack
                            key={item.id}
                            aria-selected={item.isSelected}
                            aria-checked={item.isSelected}
                            userSelect="none"
                            align="start"
                            w="full"
                            h="full"
                            borderRadius="md"
                            cursor="pointer"
                            overflow="hidden"
                            borderWidth={2}
                            borderColor="whiteAlpha.200"
                            role="group"
                            onClick={item.onClick}
                            _hover={{ bg: 'whiteAlpha.50' }}
                            _selected={{
                                bg: 'whiteAlpha.50',
                                borderColor: 'whiteAlpha.300',
                            }}
                        >
                            <HStack w="full" spacing={4}>
                                <Center h={16} w={16} bg="whiteAlpha.50" flexShrink={0}>
                                    <Icon fontSize="3xl" as={item.icon} />
                                </Center>
                                <HStack w="full" justify="space-between">
                                    <VStack align="start" spacing={0}>
                                        <Text
                                            color="whiteAlpha.800"
                                            fontWeight="semibold"
                                            noOfLines={1}
                                        >
                                            {item.label}
                                        </Text>
                                        <Text color="whiteAlpha.600" noOfLines={1}>
                                            {item.description}
                                        </Text>
                                    </VStack>
                                    <HStack
                                        px={4}
                                        // display="none"
                                        // _groupChecked={{ display: 'block' }}
                                    >
                                        <Radio isChecked={item.isSelected} />
                                    </HStack>
                                </HStack>
                            </HStack>
                        </VStack>
                    ))}
                </SimpleGrid>
            </VStack>
        );
    };

    const OptionForm: React.FC<HomeOptionViewProps> = (props) => {
        return (
            <VStack w="full" spacing={0} bg="whiteAlpha.200">
                <PopoverHeader w="full" px={3} py={2}>
                    <Text color="whiteAlpha.800" fontWeight="semibold">
                        Options
                    </Text>
                </PopoverHeader>
                <PopoverBody w="full" p={3}>
                    <VStack
                        align="start"
                        w="full"
                        divider={<StackDivider borderColor="whiteAlpha.100" />}
                        spacing={4}
                    >
                        {props.fields.map((field) => (
                            <HStack
                                key={field.id}
                                w="full"
                                justify="space-between"
                                spacing={6}
                            >
                                <VStack align="start" spacing={0.5}>
                                    <Text color="whiteAlpha.800" fontWeight="semibold">
                                        {field.label}
                                    </Text>
                                    <Text color="whiteAlpha.600">
                                        {field.description}
                                    </Text>
                                </VStack>
                                <HStack justify="end">
                                    {field.input.kind === 'switch' && (
                                        <Switch {...field.input.switch} />
                                    )}
                                    {field.input.kind === 'select' && (
                                        <Select
                                            {...field.input.select}
                                            size="sm"
                                            minW="7rem"
                                            noOfLines={1}
                                            _focus={{ outline: 'none' }}
                                            _focusVisible={{ outline: 'none' }}
                                        >
                                            {field.input.options.map((option) => (
                                                <option
                                                    key={option.value}
                                                    value={option.value}
                                                >
                                                    {option.label}
                                                </option>
                                            ))}
                                        </Select>
                                    )}
                                </HStack>
                            </HStack>
                        ))}
                    </VStack>
                </PopoverBody>
                <PopoverFooter w="full">
                    <HStack w="full" justify="end">
                        <Button {...props.submit.button} size="sm" colorScheme="green">
                            {props.submit.label}
                        </Button>
                    </HStack>
                </PopoverFooter>
            </VStack>
        );
    };

    const OptionPopover: React.FC<ReportGenerateViewProps['options']> = (props) => {
        return (
            <Popover
                {...props.popover}
                // NOTE unmount to reset local state
                lazyBehavior="unmount"
                isLazy={true}
            >
                <PopoverTrigger>
                    <IconButton
                        bg="none"
                        _hover={{
                            bg: 'whiteAlpha.50',
                        }}
                        size="sm"
                        color="whiteAlpha.600"
                        aria-label="Options"
                        icon={<Icon fontSize="lg" as={MdSettings} />}
                    />
                </PopoverTrigger>
                <PopoverContent
                    w="24rem"
                    bg="black"
                    p={0}
                    overflow="hidden"
                    fontSize="sm"
                    _focus={{
                        outline: 'none',
                    }}
                    _focusVisible={{
                        outline: 'none',
                    }}
                >
                    <OptionContainer as={OptionForm} {...props.container} />
                </PopoverContent>
            </Popover>
        );
    };

    return (props) => {
        const {
            ref: inputRegisterRef,
            name,
            onChange,
            onBlur,
        } = props.fileInput.inputProps;
        const filesArray = props.fileInput.selectedFiles || [];
        const filesToDisplay = filesArray.slice(0, MAX_DISPLAY_FILES);
        const remainingFilesCount = filesArray.length - filesToDisplay.length;

        return (
            <VStack w="full" align="start" px={6}>
                <VStack
                    w="full"
                    align="center"
                    pt={{ base: 2, md: 3, lg: 4, xl: 6 }}
                    pb={{ base: 24, xl: 44 }}
                    spacing={{ base: 4, md: 6, lg: 8, xl: 12 }}
                >
                    <VStack
                        maxW="48rem"
                        w="full"
                        align="start"
                        spacing={8}
                        // py={{ base: 4, md: 8, lg: 8 }}
                        py={4}
                    >
                        <VStack align="start" spacing={8} w="full">
                            <HStack
                                borderRadius="full"
                                p={4}
                                borderWidth={2}
                                borderColor="whiteAlpha.200"
                                justify="center"
                                align="center"
                            >
                                <Image
                                    pos="relative"
                                    left={-0.5}
                                    top={0.5}
                                    w={6}
                                    h={6}
                                    src={Graphics.Brand.Icon}
                                />
                            </HStack>
                            <VStack align="start" spacing={2}>
                                <Heading
                                    fontWeight="semibold"
                                    fontSize="3xl"
                                    color="whiteAlpha.900"
                                >
                                    Start your research
                                </Heading>
                                {/* <Text
                                        color="whiteAlpha.700"
                                        fontSize="xl"
                                        maxW="32rem"
                                    >
                                        Ask a question or describe a task you want to
                                        accomplish
                                    </Text> */}
                            </VStack>
                        </VStack>
                        <VStack w="full" spacing={8}>
                            <TemplateSelectContainer
                                as={TemplateSelect}
                                {...props.template}
                            />
                            <SimpleGrid
                                w="full"
                                gap={4}
                                templateColumns="1fr min-content 1fr"
                            >
                                <HStack h="full" w="full" align="center">
                                    <Divider
                                        borderWidth={1}
                                        borderColor="whiteAlpha.300"
                                    />
                                </HStack>
                                <Text
                                    textTransform="uppercase"
                                    fontWeight="bold"
                                    fontSize="xs"
                                    color="whiteAlpha.400"
                                >
                                    Or
                                </Text>
                                <HStack h="full" w="full" align="center">
                                    <Divider
                                        borderWidth={1}
                                        borderColor="whiteAlpha.300"
                                    />
                                </HStack>
                            </SimpleGrid>

                            <Box w="full" as="form" {...props.form.getFormElementProps()}>
                                <VStack
                                    w="full"
                                    align="start"
                                    spacing={4}
                                    p={4}
                                    // borderWidth={1}
                                    // borderColor="whiteAlpha.200"
                                    borderRadius="xl"
                                    position="relative"
                                    bg="whiteAlpha.50"
                                >
                                    <ChakraTextareaAutosize
                                        {...props.field.question.input}
                                        w="full"
                                        lineHeight={1.25}
                                        autoFocus={true}
                                        minRows={1}
                                        maxRows={8}
                                        border="none"
                                        bg="none"
                                        tabIndex={1}
                                        resize="none"
                                        // placeholder="Write something"
                                        placeholder="Ask a question or describe a task you want to accomplish"
                                        _focus={{
                                            outline: 'none',
                                        }}
                                        _focusVisible={{
                                            outline: 'none',
                                        }}
                                    />
                                    {/* Display Selected Files (truncated with remove button) */}
                                    {filesArray.length > 0 && (
                                        <Wrap pt={2}>
                                            {filesToDisplay.map((file, index) => (
                                                <Tag
                                                    key={`${file.name}-${index}`}
                                                    size="md"
                                                    borderRadius="full"
                                                    variant="subtle"
                                                    colorScheme="blue"
                                                    userSelect="none"
                                                >
                                                    <TagLabel>{file.name}</TagLabel>
                                                    <TagCloseButton
                                                        onClick={props.fileInput.onRemoveFile.bind(
                                                            null,
                                                            index
                                                        )}
                                                    />
                                                </Tag>
                                            ))}
                                            {/* Show +N more tag if needed */}
                                            {remainingFilesCount > 0 && (
                                                <Tag
                                                    size="md"
                                                    borderRadius="full"
                                                    variant="subtle"
                                                    // colorScheme="gray"
                                                    userSelect="none"
                                                    bg="none"
                                                    border="none"
                                                    color="whiteAlpha.600"
                                                >
                                                    +{remainingFilesCount} more
                                                </Tag>
                                            )}
                                        </Wrap>
                                    )}
                                    <VisuallyHidden>
                                        <input
                                            type="file"
                                            accept=".md, .markdown, .csv"
                                            multiple
                                            name={name}
                                            onChange={onChange}
                                            onBlur={onBlur}
                                            ref={inputRegisterRef}
                                        />
                                    </VisuallyHidden>
                                    <HStack w="full" justify="space-between" align="end">
                                        <HStack spacing={3}>
                                            <HStack spacing={1}>
                                                <Tooltip
                                                    placement="top"
                                                    bg="gray.50"
                                                    color="gray.900"
                                                    py={1}
                                                    px={2}
                                                    borderRadius="md"
                                                    openDelay={500}
                                                    label="Add file"
                                                >
                                                    <IconButton
                                                        onFocus={(event) => {
                                                            // disable re-focus on close
                                                            event.preventDefault();
                                                        }}
                                                        bg="none"
                                                        _hover={{
                                                            bg: 'whiteAlpha.50',
                                                        }}
                                                        color="whiteAlpha.600"
                                                        aria-label="Add file"
                                                        size="sm"
                                                        icon={
                                                            <Icon
                                                                fontSize="lg"
                                                                as={RiAttachment2}
                                                            />
                                                        }
                                                        ref={props.fileInput.button.ref}
                                                        onClick={
                                                            props.fileInput.button.onClick
                                                        }
                                                    />
                                                </Tooltip>
                                                <Tooltip
                                                    placement="top"
                                                    bg="gray.50"
                                                    color="gray.900"
                                                    py={1}
                                                    px={2}
                                                    borderRadius="md"
                                                    openDelay={500}
                                                    label="Add context"
                                                >
                                                    <IconButton
                                                        bg="none"
                                                        _hover={{
                                                            bg: 'whiteAlpha.50',
                                                        }}
                                                        size="sm"
                                                        color="whiteAlpha.600"
                                                        aria-label="Add context"
                                                        icon={
                                                            <Icon
                                                                fontSize="lg"
                                                                as={
                                                                    MdOutlineAlternateEmail
                                                                }
                                                            />
                                                        }
                                                    />
                                                </Tooltip>
                                                <Tooltip
                                                    placement="top"
                                                    bg="gray.50"
                                                    color="gray.900"
                                                    py={1}
                                                    px={2}
                                                    borderRadius="md"
                                                    openDelay={500}
                                                    closeOnClick={true}
                                                    label="Options"
                                                >
                                                    <OptionPopover {...props.options} />
                                                </Tooltip>
                                            </HStack>
                                            <Menu placement="bottom" autoSelect={false}>
                                                <Tooltip
                                                    placement="top"
                                                    bg="gray.50"
                                                    color="gray.900"
                                                    py={1}
                                                    px={2}
                                                    borderRadius="md"
                                                    openDelay={500}
                                                    label="Agent mode"
                                                >
                                                    <MenuButton
                                                        as={Button}
                                                        bg="none"
                                                        color="whiteAlpha.600"
                                                        borderWidth={1}
                                                        borderColor="whiteAlpha.200"
                                                        aria-label="at"
                                                        size="sm"
                                                        borderRadius="full"
                                                        leftIcon={
                                                            <Icon
                                                                color="whiteAlpha.400"
                                                                as={MdOutlineRateReview}
                                                            />
                                                        }
                                                        rightIcon={
                                                            <Icon
                                                                color="whiteAlpha.400"
                                                                as={ChevronDownIcon}
                                                            />
                                                        }
                                                        _hover={{
                                                            bg: 'whiteAlpha.50',
                                                        }}
                                                        _expanded={{
                                                            bg: 'whiteAlpha.100',
                                                        }}
                                                    >
                                                        Manual
                                                    </MenuButton>
                                                </Tooltip>
                                                <MenuList
                                                    maxW="20rem"
                                                    bg="black"
                                                    p={0}
                                                    overflow="hidden"
                                                    fontSize="sm"
                                                >
                                                    <VStack
                                                        align="start"
                                                        w="full"
                                                        bg="whiteAlpha.200"
                                                        p={1}
                                                        spacing={0}
                                                    >
                                                        <MenuItem
                                                            w="full"
                                                            borderRadius="md"
                                                            py={1}
                                                            alignItems="start"
                                                            icon={
                                                                <Icon
                                                                    position="relative"
                                                                    top={1}
                                                                    color="whiteAlpha.400"
                                                                    fontSize="lg"
                                                                    as={
                                                                        MdOutlineRateReview
                                                                    }
                                                                />
                                                            }
                                                        >
                                                            <VStack
                                                                align="start"
                                                                spacing={0}
                                                            >
                                                                <Text
                                                                    color="whiteAlpha.800"
                                                                    fontWeight="medium"
                                                                >
                                                                    Manual
                                                                </Text>
                                                                <Text
                                                                    color="whiteAlpha.600"
                                                                    noOfLines={2}
                                                                >
                                                                    Agents will draft
                                                                    content and ask for
                                                                    approval
                                                                </Text>
                                                            </VStack>
                                                        </MenuItem>
                                                        <MenuItem
                                                            w="full"
                                                            borderRadius="md"
                                                            py={1}
                                                            alignItems="start"
                                                            icon={
                                                                <Icon
                                                                    position="relative"
                                                                    top={1}
                                                                    fontSize="lg"
                                                                    color="whiteAlpha.400"
                                                                    as={
                                                                        AiOutlineThunderbolt
                                                                    }
                                                                />
                                                            }
                                                        >
                                                            <VStack
                                                                align="start"
                                                                spacing={0}
                                                            >
                                                                <Text
                                                                    color="whiteAlpha.800"
                                                                    fontWeight="medium"
                                                                >
                                                                    Automatic
                                                                </Text>
                                                                <Text
                                                                    color="whiteAlpha.600"
                                                                    noOfLines={2}
                                                                >
                                                                    Agent will
                                                                    autonomously create
                                                                    content without
                                                                    approval
                                                                </Text>
                                                            </VStack>
                                                        </MenuItem>
                                                    </VStack>
                                                </MenuList>
                                            </Menu>
                                        </HStack>
                                        <HStack>
                                            <IconButton
                                                {...props.submit.button}
                                                aria-label="continue"
                                                icon={
                                                    <Icon
                                                        fontSize="3xl"
                                                        as={BsArrowRightShort}
                                                    />
                                                }
                                                size="md"
                                                borderRadius="full"
                                                bg="white"
                                                color="black"
                                                _hover={{
                                                    bg: 'whiteAlpha.900',
                                                }}
                                                _disabled={{ opacity: 0.3 }}
                                            />
                                        </HStack>
                                    </HStack>
                                </VStack>
                            </Box>
                        </VStack>
                    </VStack>
                </VStack>
            </VStack>
        );
    };
}
